import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import styles from "../../css/Discussions.module.css";
import { useAuth } from '../../contexts/AuthContext';


const BASE_URL = "https://techservicemarket-backend.onrender.com"


// Helper function to get the Authorization header.
const getAuthHeader = () => {
  const token = localStorage.getItem("token");
  return { Authorization: `Bearer ${token}` };
};

const Discussions = () => {
  
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [newThread, setNewThread] = useState({ title: "", body: "", tags: "" });
  const [tagsFilter, setTagsFilter] = useState("");
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");
  const [userId, setUserId] = useState(null);

  // Selected thread detail
  const [selectedThread, setSelectedThread] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadError, setThreadError] = useState("");
  const [editThread, setEditThread] = useState(false);
  const [editedThread, setEditedThread] = useState({ title: "", body: "", tags: "" });

  // Replies
  const [newReply, setNewReply] = useState("");
  const [editingReplies, setEditingReplies] = useState({});

  const { userRole } = useAuth();
  const navigate = useNavigate();

 
  // useEffect - Check Token, Fetch User & Threads
  
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }
    fetchUser();
    fetchThreads();
  }, [navigate]);

  useEffect(() => {
    // Redirect based on user role
    if (userRole === 'recruiter') {
      navigate('/recruiter/dashboard');
      return;
    } else if (userRole === 'universitypartner') {
      navigate('/university/dashboard');
      return;
    }
    
    // Rest of your useEffect
  }, [navigate, userRole]);

  // Handle Auth Errors

  const handleAuthError = (err) => {
    if (err.response && [401, 403].includes(err.response.status)) {
      localStorage.removeItem("token");
      navigate("/login");
    }
  };


  // Fetch Current User

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/api/users/profile`, {
        headers: getAuthHeader(),
      });
      const currentUserId = response.data._id || response.data.id;
      setUserId(currentUserId);
      localStorage.setItem("userId", currentUserId);
    } catch (err) {
      console.error("Error fetching user:", err.response?.data || err);
      handleAuthError(err);
    }
  };


  // Fetch Threads

  const fetchThreads = async () => {
    try {
      setLoading(true);
      setError("");

      let query = `?sortBy=${sortBy}&order=${sortOrder}`;
      if (tagsFilter.trim()) {
        query += `&tags=${encodeURIComponent(tagsFilter.trim())}`;
      }

      const response = await axios.get(`${BASE_URL}/api/threads${query}`, {
        headers: getAuthHeader(),
      });
      setThreads(response.data);
    } catch (err) {
      console.error("Error fetching threads:", err.response?.data || err);
      setError("Failed to fetch threads");
      handleAuthError(err);
    } finally {
      setLoading(false);
    }
  };


  // Create Thread

  const handleCreateThread = async () => {
    try {
      const threadData = {
        title: newThread.title,
        body: newThread.body,
        tags: newThread.tags,
      };
      const response = await axios.post(`${BASE_URL}/api/threads`, threadData, {
        headers: getAuthHeader(),
      });
      setThreads((prev) => [response.data, ...prev]);
      setNewThread({ title: "", body: "", tags: "" });
      setError("");
    } catch (err) {
      console.error("Error creating thread:", err.response?.data || err);
      setError("Failed to create thread");
      handleAuthError(err);
    }
  };


  // View Thread Details

  const viewThreadDetails = async (threadId) => {
    try {
      setThreadLoading(true);
      setThreadError("");
      const response = await axios.get(`${BASE_URL}/api/threads/${threadId}`, {
        headers: getAuthHeader(),
      });
      const threadDetails = response.data.thread;
      threadDetails.replies = response.data.replies;
      setSelectedThread(threadDetails);
      setEditedThread({
        title: threadDetails.title,
        body: threadDetails.body,
        tags: threadDetails.tags.join(", "),
      });
    } catch (err) {
      console.error("Error fetching thread details:", err.response?.data || err);
      setThreadError("Failed to load thread details");
      handleAuthError(err);
    } finally {
      setThreadLoading(false);
    }
  };


  // Update Thread

  const handleUpdateThread = async () => {
    if (!selectedThread) return;
    try {
      const data = {
        title: editedThread.title,
        body: editedThread.body,
        tags: editedThread.tags,
      };
      const response = await axios.put(
        `${BASE_URL}/api/threads/${selectedThread._id}`,
        data,
        { headers: getAuthHeader() }
      );
      setSelectedThread(response.data);
      setEditThread(false);
      // Update in the list
      setThreads((prev) =>
        prev.map((t) => (t._id === response.data._id ? response.data : t))
      );
    } catch (err) {
      console.error("Error updating thread:", err.response?.data || err);
      setThreadError("Failed to update thread");
      handleAuthError(err);
    }
  };


  // Delete Thread

  const handleDeleteThread = async (threadId) => {
    try {
      await axios.delete(`${BASE_URL}/api/threads/${threadId}`, {
        headers: getAuthHeader(),
      });
      setThreads((prev) => prev.filter((t) => t._id !== threadId));
      if (selectedThread && selectedThread._id === threadId) {
        setSelectedThread(null);
      }
    } catch (err) {
      console.error("Error deleting thread:", err.response?.data || err);
      setThreadError("Failed to delete thread");
      handleAuthError(err);
    }
  };


  // Create Reply
 
  const handleCreateReply = async () => {
    if (!selectedThread) return;
    try {
      const response = await axios.post(
        `${BASE_URL}/api/threads/${selectedThread._id}/replies`,
        { replyBody: newReply },
        { headers: getAuthHeader() }
      );
      setSelectedThread((prev) => ({
        ...prev,
        replies: [...prev.replies, response.data],
      }));
      setNewReply("");
    } catch (err) {
      console.error("Error creating reply:", err.response?.data || err);
      setThreadError("Failed to create reply");
      handleAuthError(err);
    }
  };

 
  // Update Reply

  const handleUpdateReply = async (replyId) => {
    if (!selectedThread) return;
    const newReplyBody = editingReplies[replyId];
    try {
      const response = await axios.put(
        `${BASE_URL}/api/threads/${selectedThread._id}/replies/${replyId}`,
        { replyBody: newReplyBody },
        { headers: getAuthHeader() }
      );
      setSelectedThread((prev) => ({
        ...prev,
        replies: prev.replies.map((r) => (r._id === replyId ? response.data : r)),
      }));
      setEditingReplies((prev) => ({ ...prev, [replyId]: "" }));
    } catch (err) {
      console.error("Error updating reply:", err.response?.data || err);
      setThreadError("Failed to update reply");
      handleAuthError(err);
    }
  };


  // Delete Reply

  const handleDeleteReply = async (replyId) => {
    if (!selectedThread) return;
    try {
      await axios.delete(
        `${BASE_URL}/api/threads/${selectedThread._id}/replies/${replyId}`,
        { headers: getAuthHeader() }
      );
      setSelectedThread((prev) => ({
        ...prev,
        replies: prev.replies.filter((r) => r._id !== replyId),
      }));
    } catch (err) {
      console.error("Error deleting reply:", err.response?.data || err);
      setThreadError("Failed to delete reply");
      handleAuthError(err);
    }
  };


  // React (Like/Unlike) Reply

  const handleReactReply = async (reply) => {
    if (!selectedThread) return;
    try {
      const alreadyReacted = reply.reactions.some(
        (r) => r.userId.toString() === userId
      );
      if (alreadyReacted) {
        const response = await axios.delete(
          `${BASE_URL}/api/threads/${selectedThread._id}/replies/${reply._id}/react`,
          { headers: getAuthHeader() }
        );
        setSelectedThread((prev) => ({
          ...prev,
          replies: prev.replies.map((r) => (r._id === reply._id ? response.data : r)),
        }));
      } else {
        const response = await axios.post(
          `${BASE_URL}/api/threads/${selectedThread._id}/replies/${reply._id}/react`,
          { reactionType: "like" },
          { headers: getAuthHeader() }
        );
        setSelectedThread((prev) => ({
          ...prev,
          replies: prev.replies.map((r) => (r._id === reply._id ? response.data : r)),
        }));
      }
    } catch (err) {
      console.error("Error reacting to reply:", err.response?.data || err);
      setThreadError("Failed to update reaction");
      handleAuthError(err);
    }
  };

  if (userRole !== 'user') {
    return <div className={styles.loading}>Redirecting...</div>;
  }

  // Render: If a Thread Is Selected (Thread Detail)

  if (selectedThread) {
    return (
      <><div className={styles.buttonGroup}>
        <button onClick={() => setSelectedThread(null)} className={styles.button}>
          Back to Threads
        </button>
      </div><div className={styles.discussionsContainer}>

          {threadError && <p className={styles.errorText}>{threadError}</p>}
          {threadLoading ? (
            <p>Loading thread details...</p>
          ) : (
            <div>
              {editThread ? (
                <div>
                  <h2>Edit Thread</h2>
                  <input
                    type="text"
                    value={editedThread.title}
                    onChange={(e) => setEditedThread({ ...editedThread, title: e.target.value })}
                    placeholder="Title"
                    className={styles.input} />
                  <textarea
                    value={editedThread.body}
                    onChange={(e) => setEditedThread({ ...editedThread, body: e.target.value })}
                    placeholder="Body"
                    className={styles.textarea} />
                  <input
                    type="text"
                    value={editedThread.tags}
                    onChange={(e) => setEditedThread({ ...editedThread, tags: e.target.value })}
                    placeholder="Tags (comma-separated)"
                    className={styles.input} />
                  <button onClick={handleUpdateThread} className={styles.button}>
                    Save
                  </button>
                  <button onClick={() => setEditThread(false)} className={styles.button}>
                    Cancel
                  </button>
                </div>
              ) : (
                <div>
                  <h2>{selectedThread.title}</h2>
                  <p>{selectedThread.body}</p>
                  <p>
                    <strong>Tags:</strong> {selectedThread.tags.join(", ")}
                  </p>
                  <p>
                    <strong>Posted by:</strong>{" "}
                    {selectedThread.author && selectedThread.author.username
                      ? selectedThread.author.username
                      : "Unknown"}{" "}
                    on {new Date(selectedThread.createdAt).toLocaleString()}
                  </p>
                  <p>
                    <strong>Replies:</strong> {selectedThread.replyCount} |{" "}
                    <strong>Views:</strong> {selectedThread.viewCount}
                  </p>
                  {userId &&
                    selectedThread.author &&
                    userId === selectedThread.author._id?.toString() && (
                      <div>
                        <button onClick={() => setEditThread(true)} className={styles.button}>
                          Edit Thread
                        </button>
                        <button
                          onClick={() => handleDeleteThread(selectedThread._id)}
                          className={styles.deleteButton}
                        >
                          Delete Thread
                        </button>
                      </div>
                    )}
                </div>
              )}
              <hr />
              <h3>Replies</h3>
              {selectedThread.replies && selectedThread.replies.length > 0 ? (
                selectedThread.replies.map((reply) => (
                  <div key={reply._id} className={styles.replyCard}>
                    <p>{reply.replyBody}</p>
                    <p>
                      <strong>Replied by:</strong>{" "}
                      {reply.author && reply.author.username
                        ? reply.author.username
                        : "Unknown"}{" "}
                      on {new Date(reply.createdAt).toLocaleString()}
                    </p>
                    <p>
                      <strong>Likes:</strong>{" "}
                      {reply.reactions
                        ? reply.reactions.filter((r) => r.reactionType === "like").length
                        : 0}
                    </p>
                    {userId &&
                      reply.author &&
                      userId === reply.author._id?.toString() && (
                        <div>
                          <textarea
                            value={editingReplies[reply._id] !== undefined
                              ? editingReplies[reply._id]
                              : reply.replyBody}
                            onChange={(e) => setEditingReplies({
                              ...editingReplies,
                              [reply._id]: e.target.value,
                            })}
                            className={styles.textarea} />
                          <div className={styles.buttonGroup}>
                            <button
                              onClick={() => handleUpdateReply(reply._id)}
                              className={styles.button}
                            >
                              Save Reply
                            </button>
                            <button
                              onClick={() => handleDeleteReply(reply._id)}
                              className={styles.deleteButton}
                            >
                              Delete Reply
                            </button>
                          </div>
                        </div>
                      )}
                    <div className={styles.buttonGroup}>
                    <button onClick={() => handleReactReply(reply)} className={styles.button}>
                      {reply.reactions &&
                        reply.reactions.some((r) => r.userId.toString() === userId)
                        ? "Unlike"
                        : "Like"}
                    </button>
                    </div>
                  </div>
                ))
              ) : (
                <p>No replies yet.</p>
              )}
              <div>
                <h3>Add a Reply</h3>
                <textarea
                  value={newReply}
                  onChange={(e) => setNewReply(e.target.value)}
                  placeholder="Write a reply..."
                  className={styles.textarea} />
                <div className={styles.buttonGroup}>
                <button onClick={handleCreateReply} className={styles.button}>
                  Post Reply
                </button>
                </div>
              </div>
            </div>
          )}
        </div></>
    );
  }


  // Render: Threads List, Create Thread Form, Filter Options

  return (
    <div className={styles.discussionsContainer}>
      {/* Header */}
      <h2 className={styles.discussionsHeader}>Discussion Threads</h2>
      {error && <p className={styles.errorText}>{error}</p>}

      <div className={styles.contentContainer}>
        {/* Create a New Thread Section */}
        <div className={styles.threadCard}>
          <h3>Create a New Thread</h3>
          <input
            type="text"
            value={newThread.title}
            onChange={(e) => setNewThread({ ...newThread, title: e.target.value })}
            placeholder="Thread Title"
            className={styles.input}
          />
          <textarea
            value={newThread.body}
            onChange={(e) => setNewThread({ ...newThread, body: e.target.value })}
            placeholder="Thread Body"
            className={styles.textarea}
          />
          <input
            type="text"
            value={newThread.tags}
            onChange={(e) => setNewThread({ ...newThread, tags: e.target.value })}
            placeholder="Tags (comma-separated)"
            className={styles.input}
          />
          <div className={styles.buttonGroup}>
          <button onClick={handleCreateThread} className={styles.button}>
            Create Thread
          </button>
          </div>
        </div>

        {/* Filter/Sort Section */}
        <div className={styles.searchSection}>
          <input
            type="text"
            value={tagsFilter}
            onChange={(e) => setTagsFilter(e.target.value)}
            placeholder="Filter by tags"
            className={styles.searchInput}
          />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className={styles.selectInput}
          >
            <option value="date">Date</option>
            <option value="engagement">Engagement</option>
          </select>
          <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            className={styles.selectInput}
          >
            <option value="desc">Descending</option>
            <option value="asc">Ascending</option>
          </select>
          <button onClick={fetchThreads} className={styles.searchButton}>
            Apply
          </button>
        </div>
      </div>

      {/* Threads List */}
      {loading ? (
        <p>Loading threads...</p>
      ) : (
        <div>
          {threads.map((thread) => (
            <div key={thread._id} className={styles.threadCard}>
              <h3>{thread.title}</h3>
              <p>{thread.body}</p>
              <p>
                <strong>Owned by:</strong>{" "}
                {thread.author && thread.author.username
                  ? thread.author.username
                  : "Unknown"}{" "}
                on {new Date(thread.createdAt).toLocaleString()}
              </p>
              <p>
                <strong>Replies:</strong> {thread.replyCount} |{" "}
                <strong>Views:</strong> {thread.viewCount}
              </p>
              <div className={styles.buttonGroup}>
              <button
                onClick={() => viewThreadDetails(thread._id)}
                className={styles.button}
              >
                View Thread
              </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Discussions;
